import React, { useState, useEffect } from "react";
import { Modal } from "antd";
import { MdMobileFriendly, MdSupportAgent } from "react-icons/md";
import DSButton from "../../DSButton";
import { useClient } from "../../../hooks/ClientProvider";
import api from "../../../services/api";
import { notifySucces, notifyError } from "../../../utils/notifications";
import * as S from "./styles";

interface ModalChangePasswordChannelProps {
  open: boolean;
  closeModal: () => void;
  clientId: number;
  additionalClientId?: number;
  cardId?: number;
  clientCpf: string;
  pushEnabled: boolean;
  sacEnabled: boolean;
  onSelectSacFlow: () => void;
}

export function ModalChangePasswordChannel({
  open,
  closeModal,
  clientId,
  additionalClientId,
  cardId,
  clientCpf,
  pushEnabled,
  sacEnabled,
  onSelectSacFlow,
}: ModalChangePasswordChannelProps) {
  const { refreshClientV2 } = useClient();
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState<"APP" | "SAC" | null>(null);

  useEffect(() => {
    if (!open) return;

    if (pushEnabled && !sacEnabled) {
      setSelectedOption("APP");
    } else if (sacEnabled && !pushEnabled) {
      setSelectedOption("SAC");
    } else {
      setSelectedOption(null);
    }
  }, [open, pushEnabled, sacEnabled]);

  const handleSendPush = async () => {
    if (!clientCpf) {
      notifyError("CPF do portador não encontrado");
      return;
    }

    try {
      setLoading(true);

      const response = await api.post("push-api/notificacoes", {
        jornada: "redefinicao_senha",
        cpf: clientCpf,
        client_id: clientId,
        additional_id: additionalClientId,
        card_id: cardId,
        titulo: "Atualize sua senha com segurança",
        descricao:
          "Acesse o app para revisar ou redefinir seus dados de forma segura. Toque aqui para continuar.",
      });

      const statusCode = Number(response?.statusCode);
      if (Number.isFinite(statusCode) && statusCode !== 200) {
        notifyError(
          response?.detail ||
            response?.message ||
            "Falha ao solicitar alteração de senha via aplicativo"
        );
        return;
      }

      notifySucces("Link para alteração de senha enviado com sucesso", true);
      if (refreshClientV2) await refreshClientV2();
      closeModal();
    } catch (error: any) {
      notifyError(
        error?.message || "Falha ao solicitar alteração de senha via aplicativo"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    if (selectedOption === "APP") {
      handleSendPush();
    } else if (selectedOption === "SAC") {
      closeModal();
      if (onSelectSacFlow) onSelectSacFlow();
    }
  };

  return (
    <Modal
      open={open}
      onCancel={closeModal}
      footer={null}
      destroyOnClose
      width={580}
      title={<S.ModalHeaderTitle>Alterar Senha</S.ModalHeaderTitle>}
    >
      <S.Container>
        <S.Subtitle>Como deseja alterar senha?</S.Subtitle>

        <S.CardsGrid>
          <S.OptionCard
            type="button"
            active={selectedOption === "APP"}
            disabled={!pushEnabled || loading}
            onClick={() => pushEnabled && setSelectedOption("APP")}
            data-testid="btn_alterar_senha_via_app"
          >
            {!pushEnabled && <S.DisabledTag>Indisponível</S.DisabledTag>}
            <S.IconWrapper active={selectedOption === "APP"} disabled={!pushEnabled}>
              <MdMobileFriendly size={36} />
            </S.IconWrapper>
            <S.CardTitle active={selectedOption === "APP"} disabled={!pushEnabled}>
              Alterar Via App
            </S.CardTitle>
          </S.OptionCard>

          <S.OptionCard
            type="button"
            active={selectedOption === "SAC"}
            disabled={!sacEnabled || loading}
            onClick={() => sacEnabled && setSelectedOption("SAC")}
            data-testid="btn_alterar_senha_via_sac"
          >
            {!sacEnabled && <S.DisabledTag>Indisponível</S.DisabledTag>}
            <S.IconWrapper active={selectedOption === "SAC"} disabled={!sacEnabled}>
              <MdSupportAgent size={36} />
            </S.IconWrapper>
            <S.CardTitle active={selectedOption === "SAC"} disabled={!sacEnabled}>
              Alterar Via SAC
            </S.CardTitle>
          </S.OptionCard>
        </S.CardsGrid>

        <S.FooterActions>
          <DSButton
            type="button"
            buttonText="Cancelar"
            buttonType="secondary"
            isCancelType
            onClick={closeModal}
            height={30}
            style={{ padding: "0.4rem 1.5rem" }}
            dataTestId="btn_cancelar_modal_canal_senha"
            disabled={loading}
            isDisabled={loading}
          />

          <DSButton
            type="button"
            buttonText="Confirmar"
            buttonType="secondary"
            onClick={handleConfirm}
            height={30}
            style={{ padding: "0.4rem 1.5rem" }}
            dataTestId="btn_confirmar_modal_canal_senha"
            isLoading={loading}
            disabled={!selectedOption || loading}
            isDisabled={!selectedOption || loading}
          />
        </S.FooterActions>
      </S.Container>
    </Modal>
  );
}

export default ModalChangePasswordChannel;